import { motion } from 'framer-motion';
import { useStars } from '../context/StarContext';
import StillButton from './ui/StillButton';
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Mobile check wrapper component
const MobileAwareOverlay = ({ visible = true }) => {
    const [isMobileView, setIsMobileView] = useState(false);
    const location = useLocation();
    const isMessagesScreen = location.pathname === '/messages';
    
    // Check for mobile view on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth < 800;
            setIsMobileView(mobile);
        };
        
        // Check immediately
        checkMobile();
        
        // Add resize listener
        window.addEventListener('resize', checkMobile);
        
        // Add CSS rule to hide overlay on mobile ONLY for messages screen
        const style = document.createElement('style');
        style.textContent = `
            @media (max-width: 799px) {
                /* Force hide the master overlay on mobile ONLY for messages screen */
                body[data-current-path="/messages"] .fixed.inset-0.z-\\[35\\].text-center.text-still-white {
                    display: none !important;
                    opacity: 0 !important;
                    visibility: hidden !important;
                    pointer-events: none !important;
                }
            }
        `;
        document.head.appendChild(style);
        
        // Set a data attribute on the body to track current path
        document.body.setAttribute('data-current-path', location.pathname);
        
        return () => {
            window.removeEventListener('resize', checkMobile);
            document.head.removeChild(style);
        };
    }, [location.pathname]);
    
    // Update body data attribute when location changes
    useEffect(() => {
        document.body.setAttribute('data-current-path', location.pathname);
    }, [location.pathname]);
    
    // If we're on mobile AND on the messages screen, don't render the overlay
    if (isMobileView && isMessagesScreen) {
        return null;
    }
    
    // Otherwise, render the actual overlay component
    return <StillLovedMasterOverlay visible={visible} />;
};

const StillLovedMasterOverlay = ({ visible = true }) => {
    const { loading, totalWeeks } = useStars();
    const location = useLocation();
    const [isVisible, setIsVisible] = useState(visible);
    const [displayedCount, setDisplayedCount] = useState(0);
    const intervalRef = useRef(null);
    const animationStartedRef = useRef(false);
    const animationProgressRef = useRef(0); // Track animation progress from 0 to 1
    const previousTotalWeeksRef = useRef(0);
    const previousVisibilityRef = useRef(visible);
    const animationInProgressRef = useRef(false); // Track if animation is currently running
    const overlayFadedInRef = useRef(false); // Track if overlay has fully faded in
    const locationRef = useRef(location.pathname); // Track location changes
    
    // Check if we're on the messages screen
    const isMessagesScreen = location.pathname === '/messages';
    const isLandingScreen = location.pathname === '/';
    
    // Update visibility based on props and location
    useEffect(() => {
        // Check for the sessionStorage flag
        const shouldBeVisible = sessionStorage.getItem('overlayVisible') === 'true';
        const animationCompleted = sessionStorage.getItem('animationCompleted') === 'true';
        
        // Special case for messages screen - always visible
        /* if (location.pathname === '/messages') {
            setIsVisible(true);
        }  */
        // For landing page, check both the prop and the sessionStorage flag
        if (location.pathname === '/') {
            const newVisibility = visible || shouldBeVisible;
            setIsVisible(newVisibility);
        } 
        // For all other screens, respect the visible prop
        else {
            setIsVisible(visible);
        }
        
        // Check if visibility changed from visible to hidden
        if (previousVisibilityRef.current && !visible && animationStartedRef.current && !animationCompleted) {
            // Animation was interrupted - skip to the end
            
            // Clear any existing animation
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            
            // Set the final count
            setDisplayedCount(totalWeeks);
            
            // Mark animation as completed
            sessionStorage.setItem('animationCompleted', 'true');
            sessionStorage.setItem('lastDisplayedCount', totalWeeks.toString());
        }
        
        // Update previous visibility ref
        previousVisibilityRef.current = visible;
        
        // Update location ref
        locationRef.current = location.pathname;
    }, [visible, location.pathname, totalWeeks]);

    // Check if we're returning from landing screen via logo button
    useEffect(() => {
        // Only reset animation state if we're on the landing page and coming from another page
        if (location.pathname === '/' && locationRef.current !== '/' && !sessionStorage.getItem('overlayVisible')) {
            sessionStorage.removeItem('animationCompleted');
            sessionStorage.removeItem('lastDisplayedCount');
        }
        
        // Update location ref
        locationRef.current = location.pathname;
    }, [location.pathname]);

    // Custom easing function for the animation
    const customEase = (x) => {
        // Extremely slow start (first 15%)
        if (x < 0.15) {
            // Use a higher power function for an extremely slow start
            return Math.pow(x, 4) * 30; // Quartic ease-in, heavily scaled to start very slowly
        }
        // Slow-to-medium transition (next 15%)
        else if (x < 0.3) {
            // Gradually accelerate from the very slow start
            const t = (x - 0.15) / 0.15; // Normalize to 0-1 for this segment
            return 0.05 + t * t * 0.05; // Start from where the previous segment ended
        }
        // Medium speed (middle 50%)
        else if (x < 0.8) {
            return 0.1 + (x - 0.3) * 1.5; // Linear with offset
        }
        // Slow end (last 20%)
        else {
            const t = (x - 0.8) * 5; // Scale to 0-1 range for this segment
            return 0.85 + (1 - Math.pow(1 - t, 3)) * 0.15; // Cubic ease-out, scaled
        }
    };

    // Initialize displayed count from session storage or set to 0
    useEffect(() => {
        const animationCompleted = sessionStorage.getItem('animationCompleted') === 'true';
        const lastDisplayedCount = parseInt(sessionStorage.getItem('lastDisplayedCount'), 10) || 0;
        
        if (animationCompleted && lastDisplayedCount > 0) {
            setDisplayedCount(lastDisplayedCount);
            animationStartedRef.current = true;
            animationProgressRef.current = 1; // Mark as completed
        } else if (isLandingScreen) {
            // Only reset to 0 on landing screen if animation not completed
            setDisplayedCount(0);
        } else if (lastDisplayedCount > 0) {
            // On other screens, use the last displayed count if available
            setDisplayedCount(lastDisplayedCount);
        } else {
            // Default to total weeks if no last displayed count
            setDisplayedCount(totalWeeks);
        }
        
        previousTotalWeeksRef.current = totalWeeks;
    }, [totalWeeks, isLandingScreen]);

    // Effect to handle the overlay fade-in animation completion
    useEffect(() => {
        if (isVisible) {
            // When the overlay becomes visible, wait for the fade-in animation to complete
            const fadeInDuration = 1000; // 1 second fade-in duration (matches containerVariants)
            
            // Set a timeout to mark the overlay as fully faded in
            const fadeInTimer = setTimeout(() => {
                overlayFadedInRef.current = true;
                
                // Only start animation on landing screen and if not already completed
                if (isLandingScreen && !animationInProgressRef.current && !sessionStorage.getItem('animationCompleted')) {
                    startAnimation();
                }
            }, fadeInDuration);
            
            return () => {
                clearTimeout(fadeInTimer);
            };
        } else {
            // When the overlay becomes hidden, mark it as not faded in
            overlayFadedInRef.current = false;
        }
    }, [isVisible, isLandingScreen]);

    // Advanced animation using setInterval with variable speed
    useEffect(() => {
        // Don't start animation if not visible
        if (!isVisible) {
            return;
        }
        
        // Don't automatically start animation here - we'll start it after the fade-in is complete
        // or when explicitly triggered by the startAnimation function
        
        // Cleanup
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
                animationInProgressRef.current = false; // Mark animation as no longer in progress
            }
        };
    }, [isVisible]);

    // Add a special effect to handle visibility changes directly
    useEffect(() => {
        // If we're becoming visible and we're on the landing page, ensure animation starts from the beginning
        if (isVisible && isLandingScreen && !sessionStorage.getItem('animationCompleted')) {
            // Reset animation state
            animationStartedRef.current = false;
            animationProgressRef.current = 0;
            
            // Clear any existing animation
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
                animationInProgressRef.current = false;
            }
            
            // Reset the displayed count to 0
            setDisplayedCount(0);
            
            // Clear animation completed flag
            sessionStorage.removeItem('animationCompleted');
            sessionStorage.removeItem('lastDisplayedCount');
            
            // Set a new animation start time
            sessionStorage.setItem('animationStartTime', Date.now().toString());
            
            // The animation will be started by the fade-in completion effect
        }
    }, [isVisible, isLandingScreen]);
    
    // Function to start the animation directly
    const startAnimation = () => {
        // Only proceed if animation is not already in progress
        if (animationInProgressRef.current) {
            return;
        }
        
        // Only proceed if the overlay has fully faded in
        if (!overlayFadedInRef.current && isVisible) {
            return;
        }
        
        // Only animate on landing screen
        if (!isLandingScreen) {
            return;
        }
        
        // Reset animation state
        animationStartedRef.current = false;
        animationProgressRef.current = 0;
        
        // Clear any existing animation
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        
        // Reset the displayed count to 0
        setDisplayedCount(0);
        
        // Clear animation completed flag
        sessionStorage.removeItem('animationCompleted');
        sessionStorage.removeItem('lastDisplayedCount');
        
        // Set a new animation start time
        const now = Date.now();
        sessionStorage.setItem('animationStartTime', now.toString());
        
        // Mark animation as in progress
        animationInProgressRef.current = true;
        
        // Calculate animation duration based on the size of the number
        const baseDuration = 30000; // 30 seconds base
        const durationFactor = Math.log10(totalWeeks) / 2; // Adjust based on number size
        const animationDuration = Math.max(baseDuration, baseDuration * durationFactor);
        
        // Use a higher frequency for smoother animation
        const intervalTime = 30; // 30ms between updates (about 33fps)
        const totalSteps = animationDuration / intervalTime;
        
        // Start the interval immediately - no need for additional delay since we're already
        // waiting for the overlay to fade in
        
        // Initial increment value for the very beginning
        let initialCount = 0;
        
        intervalRef.current = setInterval(() => {
            // Increment progress
            animationProgressRef.current += 1 / totalSteps;
            
            // Apply easing function to get the actual progress
            const easedProgress = customEase(Math.min(1, animationProgressRef.current));
            
            // Calculate new count based on eased progress
            let newCount = Math.floor(easedProgress * totalWeeks);
            
            // For the first 5% of the animation, increment very slowly
            if (animationProgressRef.current < 0.05) {
                initialCount += Math.max(1, Math.floor(totalWeeks / 10000));
                newCount = Math.min(newCount, initialCount);
            }
            
            setDisplayedCount(prevCount => {
                // Ensure we're always increasing (never decreasing)
                const nextCount = Math.max(prevCount, newCount);
                
                // Store the current count in session storage
                sessionStorage.setItem('lastDisplayedCount', nextCount.toString());
                
                if (nextCount >= totalWeeks || animationProgressRef.current >= 1) {
                    // Animation complete
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    animationInProgressRef.current = false; // Mark animation as no longer in progress
                    
                    // Mark animation as completed in session storage
                    sessionStorage.setItem('animationCompleted', 'true');
                    
                    return totalWeeks;
                }
                
                return nextCount;
            });
        }, intervalTime);
    };

    // Listen for the custom event to start the animation
    useEffect(() => {
        const handleStartAnimation = (event) => {
            // Only start animation if it's not already in progress and we're on the landing screen
            if (!animationInProgressRef.current && isLandingScreen) {
                // If the overlay has fully faded in, start the animation immediately
                if (overlayFadedInRef.current) {
                    startAnimation();
                }
                // The animation will be started by the fade-in completion effect if not faded in yet
            }
        };
        
        window.addEventListener('startOverlayAnimation', handleStartAnimation);
        
        return () => {
            window.removeEventListener('startOverlayAnimation', handleStartAnimation);
        };
    }, [totalWeeks, isLandingScreen]); // Add isLandingScreen as a dependency

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 1,
                staggerChildren: 0.5,
            },
        },
        exit: {
            opacity: 0,
            transition: {
                duration: 0.5
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 5 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                duration: 2,
                ease: "easeOut"
            }
        },
    };

    // Check if we should force visibility based on sessionStorage
    const forceVisibleFromStorage = sessionStorage.getItem('overlayVisible') === 'true';
    
    // Only force visibility from storage on the landing page
    const shouldForceVisible = forceVisibleFromStorage && isLandingScreen;
    
    if (!isVisible && !shouldForceVisible) {
        return null;
    }
    
    // If we're rendering because of the storage flag, update our state
    if (!isVisible && shouldForceVisible) {
        // Use setTimeout to avoid state updates during render
        setTimeout(() => {
            setIsVisible(true);
            
            // The animation will be started by the fade-in completion effect
        }, 0);
    }
    
    // Format the count with commas
    const formattedWeeks = new Intl.NumberFormat().format(displayedCount);

    return (
        <motion.div 
            className={`fixed inset-0 z-[35] text-center text-still-white ${isMessagesScreen ? 'pointer-events-none' : ''}`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onAnimationComplete={(definition) => {
                if (definition === "visible") {
                    overlayFadedInRef.current = true;
                    
                    // Only start animation on landing screen and if not already completed
                    if (isLandingScreen && !animationInProgressRef.current && !sessionStorage.getItem('animationCompleted')) {
                        startAnimation();
                    }
                }
            }}
        >
            {/* Counter text centered in the screen */}
            <div className="absolute inset-0 flex items-center justify-center pb-20">
                <motion.h1 
                    className="text-2xl sm:text-4xl md:text-4xl lg:text-4xl font-bold text-white text-glow-still animate-fade-in pointer-events-none"
                    variants={itemVariants}
                >
                    {formattedWeeks} WEEKS STILL LOVED
                </motion.h1>
            </div>
            
            {/* Buttons fixed at the bottom - only show if not on messages screen */}
            {!isMessagesScreen && (
                <motion.div 
                    className="absolute bottom-5 left-0 right-0 flex justify-center space-x-4 pointer-events-auto mb-12 sm:mb-16 md:mb-20 lg:mb-24"
                    variants={itemVariants}
                >
                    <StillButton
                        to="/create-star"
                        aria-label="Add a Star"
                    >
                        ADD A STAR
                    </StillButton>
                    <StillButton
                        to="/find-star"
                        aria-label="Find a Star"
                    >
                        FIND A STAR
                    </StillButton>
                </motion.div>
            )}
        </motion.div>
    );
};

// Export the wrapped component
export default MobileAwareOverlay; 